// Simple UI overlay for displaying speech recognition text
export class SpeechUI {
  private container: HTMLDivElement | null = null;
  private textDisplay: HTMLDivElement | null = null;
  private statusIndicator: HTMLDivElement | null = null;

  constructor() {
    this.createUI();
  }

  private createUI(): void {
    // Create container
    this.container = document.createElement('div');
    this.container.style.position = 'fixed';
    this.container.style.bottom = '0';
    this.container.style.left = '0';
    this.container.style.right = '0';
    this.container.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    this.container.style.color = '#ffffff';
    this.container.style.padding = '12px 20px';
    this.container.style.fontFamily = 'Arial, sans-serif';
    this.container.style.fontSize = '14px';
    this.container.style.display = 'flex';
    this.container.style.alignItems = 'center';
    this.container.style.gap = '12px';
    this.container.style.zIndex = '9999';
    this.container.style.borderTop = '1px solid rgba(255, 255, 255, 0.2)';

    // Create status indicator (listening/not listening)
    this.statusIndicator = document.createElement('div');
    this.statusIndicator.style.width = '10px';
    this.statusIndicator.style.height = '10px';
    this.statusIndicator.style.borderRadius = '50%';
    this.statusIndicator.style.backgroundColor = '#888';
    this.statusIndicator.style.flexShrink = '0';

    // Create text display
    this.textDisplay = document.createElement('div');
    this.textDisplay.style.flex = '1';
    this.textDisplay.style.minHeight = '20px';
    this.textDisplay.textContent = 'Say "start" to begin...';

    this.container.appendChild(this.statusIndicator);
    this.container.appendChild(this.textDisplay);
    document.body.appendChild(this.container);
  }

  setListening(isListening: boolean): void {
    if (!this.statusIndicator) return;

    // When stopped, show gray
    if (!isListening) {
      this.statusIndicator.style.backgroundColor = '#888';
      this.statusIndicator.style.boxShadow = 'none';
    }
    // When listening but no speech detected yet, keep it gray
    // (speech detection will change it to green)
  }

  setSpeechDetecting(isDetecting: boolean): void {
    if (!this.statusIndicator) return;

    if (isDetecting) {
      // Actively detecting speech - green with glow
      this.statusIndicator.style.backgroundColor = '#00ff00';
      this.statusIndicator.style.boxShadow = '0 0 8px #00ff00';
    } else {
      // Listening but no speech - gray
      this.statusIndicator.style.backgroundColor = '#888';
      this.statusIndicator.style.boxShadow = 'none';
    }
  }

  setText(text: string): void {
    if (!this.textDisplay) return;
    this.textDisplay.textContent = text;
  }

  showInterim(text: string): void {
    if (!this.textDisplay) return;
    this.textDisplay.textContent = text;
    this.textDisplay.style.color = '#aaaaaa';
    this.textDisplay.style.fontStyle = 'italic';
  }

  showFinal(text: string): void {
    if (!this.textDisplay) return;
    this.textDisplay.textContent = `"${text}"`;
    this.textDisplay.style.color = '#ffffff';
    this.textDisplay.style.fontStyle = 'normal';

    // Fade back to default text after 3 seconds
    setTimeout(() => {
      this.setText('Say "start" to begin...');
      if (this.textDisplay) {
        this.textDisplay.style.color = '#ffffff';
        this.textDisplay.style.fontStyle = 'normal';
      }
    }, 3000);
  }

  showRecognized(text: string, isFinal: boolean): void {
    if (isFinal) {
      this.showFinal(text);
    } else {
      this.showInterim(text);
    }
  }

  showCommandMatched(command: string): void {
    if (!this.textDisplay) return;

    this.textDisplay.textContent = `✓ Command: "${command}"`;
    this.textDisplay.style.color = '#00ff00';

    // Reset color and text after 2 seconds
    setTimeout(() => {
      if (this.textDisplay) {
        this.textDisplay.style.color = '#ffffff';
        this.setText('Say "start" to begin...');
      }
    }, 2000);
  }

  showError(error: string): void {
    if (!this.textDisplay) return;

    this.textDisplay.textContent = `Error: ${error}`;
    this.textDisplay.style.color = '#ff6666';

    // Reset color and text after 3 seconds
    setTimeout(() => {
      if (this.textDisplay) {
        this.textDisplay.style.color = '#ffffff';
        this.setText('Say "start" to begin...');
      }
    }, 3000);
  }

  destroy(): void {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.container = null;
    this.textDisplay = null;
    this.statusIndicator = null;
  }
}
